import { useState, useEffect, useRef } from 'react';
import { enhanceTabletDetection } from './Helper';

/**
 * Custom hook for enhanced tablet detection that can be used across components
 * 
 * This hook provides enhanced client-side tablet detection that works better than
 * server-side detection for devices like iPads that may report as desktop in user agents.
 * It includes logic to prevent mobile devices from being detected as tablets.
 * 
 * @param {boolean} serverIsTablet - Server-side tablet detection result from context
 * @param {boolean} isMobile - Server-side mobile detection result from context
 * @returns {Object} Object containing tablet detection states and setters
 * @returns {boolean} returns.isTablet - Whether the device is detected as a tablet
 * @returns {boolean} returns.isLargeTablet - Whether the device is detected as a large tablet (iPad Pro, larger iPad Air)
 * @returns {Function} returns.setIsTablet - Function to manually set tablet state
 * @returns {Function} returns.setIsLargeTablet - Function to manually set large tablet state
 * 
 * @example
 * // Basic usage
 * const { isTablet, isLargeTablet } = useTabletDetection(serverIsTablet, isMobile);
 * 
 * @example
 * // With manual control
 * const { isTablet, isLargeTablet, setIsTablet, setIsLargeTablet } = useTabletDetection(serverIsTablet, isMobile);
 * 
 * // You can manually override the detection if needed
 * useEffect(() => {
 *   if (someCustomCondition) {
 *     setIsTablet(true);
 *   }
 * }, [someCustomCondition]);
 */
export const useTabletDetection = (serverIsTablet = false, isMobile = false) => {
  const [isTablet, setIsTablet] = useState(serverIsTablet);
  const [isLargeTablet, setIsLargeTablet] = useState(false);
  
  // Track if we've already run enhanced detection to prevent infinite loops
  const hasRunEnhancedDetection = useRef(false);
  
  // Ensure mobile devices are never detected as tablets
  useEffect(() => {
    if (isMobile) {
      setIsTablet(false);
      setIsLargeTablet(false);
    }
  }, [isMobile]);
  
  useEffect(() => {
    // Only enhance tablet detection if server-side detection didn't already identify it as mobile
    // and we haven't run it before
    if (!isMobile && !hasRunEnhancedDetection.current) {
      hasRunEnhancedDetection.current = true;
      const enhancedDetection = enhanceTabletDetection();
      
      if (enhancedDetection && enhancedDetection.isEnhancedTablet) {
        // Use the screen dimensions from enhanced detection, not viewport dimensions
        const screenWidth = enhancedDetection.screenWidth;
        const screenHeight = enhancedDetection.screenHeight;
        
        // Only set as tablet if screen dimensions are clearly tablet-like (not mobile)
        if (screenWidth >= 768 && screenHeight >= 1024) {
          setIsTablet(true);
          
          // Determine if this is a large tablet (iPad Pro, larger iPad Air) vs small tablet (iPad mini, mobile-like)
          if (screenWidth >= 1024 || screenHeight >= 1366) {
            setIsLargeTablet(true);
          }
        }
      }
    }
  }, [isMobile]);

  return {
    isTablet,
    isLargeTablet,
    setIsTablet,
    setIsLargeTablet
  };
};
